/**
 * X ADS › ACCOUNT — /x-ads/account (never gated)
 *
 * Connect X (live or sandbox), pick the ads account and funding instrument of each
 * connection, check its health, create a sandbox test ads account, disconnect. X's OAuth
 * callback returns here with ?connected=1&handle=… or ?error=….
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Building2, CheckCircle2, Clock3, CreditCard, FlaskConical, Plus, RefreshCw, ShieldAlert, Unplug } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/x-ads/DeleteButton';
import { XSelect } from '@/components/x-ads/XSelect';
import { XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import { forgetXConnection, isUsable, pickActive, setActiveXAccount } from '@/lib/x-ads/connection';
import { forgetProviderConnection } from '@/lib/provider-connection';
import { dateTime, fundingName, label, microsMoney } from '@/lib/x-ads/format';
import { XA_HOME } from '@/lib/x-ads/routes';
import { errMsg, xAdsService, type XAccount, type XAdsAccountOption, type XConfig, type XFundingInstrument, type XHealth } from '@/services/x-ads.service';

const SANDBOX_CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'SAR', 'PKR'];

const HEALTH: Record<XHealth['status'], { text: string; tone: string }> = {
  connected: { text: 'Connected', tone: 'bg-success-soft text-success' },
  needs_account: { text: 'Choose an ads account', tone: 'bg-warning-soft text-warning' },
  needs_setup: { text: 'Setup needed', tone: 'bg-warning-soft text-warning' },
  expired: { text: 'Reconnect needed', tone: 'bg-danger-soft text-danger' },
  unavailable: { text: 'Check connection', tone: 'bg-warning-soft text-warning' },
};

export default function XAdsAccountPage() {
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState<XConfig | null>(null);
  const [accounts, setAccounts] = useState<XAccount[] | null>(null);
  const [health, setHealth] = useState<Record<string, XHealth>>({});
  const [warnings, setWarnings] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState<'live' | 'sandbox' | null>(null);
  const [pickerFor, setPickerFor] = useState<XAccount | null>(null);
  const [removeFor, setRemoveFor] = useState<XAccount | null>(null);
  const [activeUid, setActiveUid] = useState<string | null>(null);

  // Return from X's consent page: toast it once, then clean the address.
  useEffect(() => {
    if (!params.has('connected') && !params.has('error')) return;
    if (params.get('connected') === '1') {
      const handle = params.get('handle');
      toast.success(handle ? `@${handle} connected. Choose the ads account to work on.` : 'X account connected. Choose the ads account to work on.');
      forgetProviderConnection('x');
    }
    if (params.get('error')) toast.error(params.get('error') as string);
    const next = new URLSearchParams(params);
    ['connected', 'handle', 'error'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const refresh = useCallback(async () => {
    setRefreshing(true); setError(null);
    try {
      const [list, cfg] = await Promise.all([xAdsService.list(), xAdsService.config()]);
      setAccounts(list); setConfig(cfg); setHealth({});
      setActiveUid(pickActive(list)?.accountUid ?? null);
      forgetXConnection();
      await Promise.all(list.map(async (a) => {
        try { const h = await xAdsService.health(a.accountUid); setHealth((s) => ({ ...s, [a.accountUid]: h })); }
        catch { setHealth((s) => ({ ...s, [a.accountUid]: { status: 'unavailable', message: 'Connection check failed. Try refreshing.', checkedAt: new Date().toISOString() } })); }
      }));
    } catch (e) { setError(errMsg(e, 'Unable to load your X connections.')); }
    finally { setRefreshing(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const connect = async (sandbox: boolean) => {
    setConnecting(sandbox ? 'sandbox' : 'live');
    try { window.location.href = (await xAdsService.oauthStart(sandbox)).url; }
    catch (e) { setError(errMsg(e)); setConnecting(null); }
  };

  const remove = async (a: XAccount) => {
    setRemoveFor(null);
    try { await xAdsService.disconnect(a.accountUid); toast.success(`@${a.screenName || a.xUserId} disconnected`); forgetProviderConnection('x'); await refresh(); }
    catch { /* the client already showed X's reason */ }
  };

  const onSaved = async (a: XAccount) => {
    setWarnings((w) => ({ ...w, [a.accountUid]: a.warnings ?? [] }));
    setActiveXAccount(a.accountUid);
    await refresh();
  };

  const configured = config?.configured !== false;
  const usable = (accounts ?? []).filter(isUsable);
  const connectButtons = configured && <>
    <Button onClick={() => connect(false)} loading={connecting === 'live'} disabled={!config || !!connecting} iconLeft={<Plus size={16} />}>Connect with X</Button>
    <Button variant="secondary" onClick={() => connect(true)} loading={connecting === 'sandbox'} disabled={!config || !!connecting} iconLeft={<FlaskConical size={15} />}>Connect with X (sandbox)</Button>
  </>;

  return <XAdsShell section="Account" description="Connect X, choose the ads account and payment method each connection works on."
    actions={<><Button variant="ghost" onClick={refresh} loading={refreshing} iconLeft={<RefreshCw size={15} />}>Refresh</Button>{connectButtons}</>}>
    {error && <XAdsNotice title="We couldn't load your X connections" error>{error} <button className="ml-2 underline" onClick={refresh}>Try again</button></XAdsNotice>}
    {config && !config.configured && <XAdsNotice title="X app keys are not set on the server">Ask your administrator to set the X Ads API key, secret and OAuth callback URL (Super Admin → Platform Integrations → X Ads, or X_ADS_CONSUMER_KEY / X_ADS_CONSUMER_SECRET / X_ADS_OAUTH_CALLBACK). Connecting is unavailable until then.</XAdsNotice>}

    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div><h3 className="xa-panel-title">Connected X accounts</h3><p className="xa-muted mt-1">Each X login, the ads account it works on and its health.</p></div>
          <span className="xa-chip">{accounts?.length ?? '—'} connection{accounts?.length === 1 ? '' : 's'}</span>
        </div>
        {!accounts && !error && <div className="skeleton h-52 rounded-2xl" aria-label="Loading connections" />}
        {accounts?.length === 0 && <Card className="py-14 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary"><Building2 size={26} /></div>
          <h3 className="text-lg font-semibold">No X account connected yet</h3>
          <p className="xa-muted mx-auto mb-6 mt-2 max-w-sm">Connect with X, then choose the ads account you want to manage. Use the sandbox to try everything without spending.</p>
          <div className="flex flex-wrap justify-center gap-2">{connectButtons}</div>
        </Card>}
        {accounts?.map((a) => <AccountCard key={a.accountUid} account={a} health={health[a.accountUid]} warnings={warnings[a.accountUid]}
          inUse={a.accountUid === activeUid} canUse={isUsable(a) && usable.length > 1} configured={configured}
          onUse={() => { setActiveXAccount(a.accountUid); setActiveUid(a.accountUid); toast.success('This connection is now in use in this browser.'); }}
          onPick={() => setPickerFor(a)} onRemove={() => setRemoveFor(a)} onReconnect={() => connect(a.sandbox)} onSaved={onSaved} />)}
      </div>
      <aside className="space-y-4">
        <Card>
          <p className="xa-eyebrow">CHECKLIST</p>
          {[
            { title: 'Connect X', text: 'Sign in with the X user that has a role on the ads account.', done: !!accounts?.length },
            { title: 'Choose an ads account', text: 'Pick the ads account and its funding instrument.', done: usable.length > 0 },
            { title: 'Build a campaign', text: 'Everything is created paused, so nothing spends until you enable it.', done: false },
          ].map((s, i) => <div key={s.title} className="mt-3 flex gap-3">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${s.done ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted'}`}>{s.done ? <CheckCircle2 size={14} /> : i + 1}</span>
            <div><p className="text-sm font-medium">{s.title}</p><p className="xa-muted">{s.text}</p></div>
          </div>)}
          {usable.length > 0 && <Link to={XA_HOME} className="mt-4 block"><Button className="w-full" iconRight={<ArrowRight size={15} />}>View campaigns</Button></Link>}
        </Card>
        <Card>
          <p className="xa-eyebrow">SANDBOX</p>
          <p className="xa-muted mt-2">A sandbox connection talks to X's test environment: no real spend, no delivery and no results. After connecting, create a test ads account to try campaigns, posts and cards. A sandbox and a live connection of the same X user are separate.</p>
        </Card>
      </aside>
    </div>

    <AdsAccountPicker account={pickerFor} onClose={() => setPickerFor(null)} onSaved={async (a) => { setPickerFor(null); await onSaved(a); }} />
    <ConfirmDialog open={!!removeFor} danger title="Disconnect this X account?" confirmLabel="Disconnect"
      message={`@${removeFor?.screenName || removeFor?.xUserId || ''} will be disconnected from the CRM. Campaigns on X are not paused or deleted.`}
      onCancel={() => setRemoveFor(null)} onConfirm={() => removeFor && remove(removeFor)} />
  </XAdsShell>;
}

function AccountCard({ account: a, health, warnings, inUse, canUse, configured, onUse, onPick, onRemove, onReconnect, onSaved }: {
  account: XAccount; health?: XHealth; warnings?: string[]; inUse: boolean; canUse: boolean; configured: boolean;
  onUse: () => void; onPick: () => void; onRemove: () => void; onReconnect: () => void; onSaved: (a: XAccount) => Promise<void>;
}) {
  const [funding, setFunding] = useState<XFundingInstrument[] | null>(null);
  const [fundingId, setFundingId] = useState(a.fundingInstrumentId ?? '');
  const [loadingFunding, setLoadingFunding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState('AED');
  const [creating, setCreating] = useState(false);
  const h = health ? HEALTH[health.status] : null;
  const current = funding?.find((f) => f.id === a.fundingInstrumentId);

  const loadFunding = async () => {
    setLoadingFunding(true);
    try { setFunding(await xAdsService.fundingInstruments(a.accountUid)); setFundingId(a.fundingInstrumentId ?? ''); }
    catch { /* toasted */ }
    finally { setLoadingFunding(false); }
  };
  const saveFunding = async () => {
    if (!a.adsAccountId || !fundingId) return;
    setSaving(true);
    try { const saved = await xAdsService.select(a.accountUid, a.adsAccountId, fundingId); toast.success('Funding instrument saved'); setFunding(null); await onSaved(saved); }
    catch { /* toasted */ }
    finally { setSaving(false); }
  };
  const createSandbox = async () => {
    setCreating(true);
    try { const saved = await xAdsService.createSandboxAccount(a.accountUid, currency); toast.success('Test ads account created and selected'); await onSaved(saved); }
    catch { /* toasted */ }
    finally { setCreating(false); }
  };

  return <Card padded={false} className="overflow-hidden">
    <div className="flex flex-wrap items-start gap-3 p-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Building2 size={20} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate text-base font-semibold">@{a.screenName || a.xUserId}</h3>
          {a.sandbox && <span className="xa-chip !bg-warning-soft !text-warning"><FlaskConical size={11} />Sandbox</span>}
          {a.isDefault && <span className="xa-chip">Default</span>}
          {inUse && <span className="xa-chip !bg-primary-soft !text-primary">In use here</span>}
          {!a.isActive && <span className="xa-chip !bg-danger-soft !text-danger">Disconnected</span>}
        </div>
        <p className="xa-muted mt-0.5">{a.adsAccountName ? `${a.adsAccountName} · ` : ''}{a.adsAccountId ? `Ads account ${a.adsAccountId}` : 'No ads account chosen yet'}</p>
      </div>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${h?.tone ?? 'bg-surface-alt text-text-muted'}`}>
        {health?.status === 'connected' ? <CheckCircle2 size={13} /> : health?.status === 'expired' ? <ShieldAlert size={13} /> : <Clock3 size={13} />}{h?.text ?? 'Checking…'}
      </span>
    </div>

    <dl className="mx-4 grid grid-cols-2 gap-3 rounded-lg bg-surface-alt/60 p-3 sm:grid-cols-4">
      {[
        ['Currency', a.currency || '—'],
        ['Time zone', a.timezone?.replace(/_/g, ' ') || '—'],
        ['Posts as', a.promotableScreenName ? `@${a.promotableScreenName}` : a.promotableUserId || '—'],
        ['Last report sync', a.lastSyncedAt ? dateTime(a.lastSyncedAt) : 'Never'],
      ].map(([k, v]) => <div key={k}><dt className="text-[11px] text-text-muted">{k}</dt><dd className="mt-1 truncate text-sm font-medium">{v}</dd></div>)}
    </dl>

    {!!a.permissions?.length && <div className="mx-4 mt-3 flex flex-wrap gap-1.5">{a.permissions.map((p) => <span key={p} className="xa-chip">{label(p)}</span>)}</div>}
    {health && health.status !== 'connected' && <p className="mx-4 mt-3 text-xs text-warning">{health.message}</p>}
    {a.lastSyncError && <p className="mx-4 mt-2 text-xs text-danger">Last sync: {a.lastSyncError}</p>}
    {!!warnings?.length && <div className="mx-4 mt-3">{warnings.map((w) => <p key={w} className="flex items-start gap-1.5 text-xs text-warning"><ShieldAlert size={13} className="mt-0.5 shrink-0" />{w}</p>)}</div>}

    {a.adsAccountId && a.isActive && <div className="mx-4 mt-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm"><CreditCard size={15} className="text-text-muted" />Funding instrument: <span className="font-medium">{current ? fundingName(current) : a.fundingInstrumentId || 'None'}</span></p>
        {!funding && <Button variant="ghost" size="sm" loading={loadingFunding} onClick={loadFunding}>Change</Button>}
      </div>
      {funding && <div className="mt-2 flex flex-wrap items-center gap-2">
        {funding.length === 0
          ? <p className="xa-muted">This ads account has no funding instrument. Add a payment method on ads.x.com.</p>
          : <div className="w-80 max-w-full"><XSelect value={fundingId} onChange={(v) => v && setFundingId(v)}
              options={funding.map((f) => ({
                value: f.id,
                label: `${fundingName(f)}${f.currency ? ` · ${f.currency}` : ''}${f.creditLimitLocalMicro ? ` · limit ${microsMoney(f.creditLimitLocalMicro, f.currency)}` : ''}${f.ableToFund ? '' : ' · cannot fund'}${f.entityStatus && f.entityStatus !== 'ACTIVE' ? ` · ${label(f.entityStatus)}` : ''}`,
              }))} /></div>}
        {funding.length > 0 && <Button size="sm" loading={saving} disabled={!fundingId || fundingId === a.fundingInstrumentId} onClick={saveFunding}>Save</Button>}
        <Button variant="ghost" size="sm" onClick={() => setFunding(null)}>Cancel</Button>
      </div>}
    </div>}

    {a.sandbox && a.isActive && configured && <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border p-3">
      <span className="text-sm">Create a test ads account (sandbox only, no cost):</span>
      <div className="w-28"><XSelect value={currency} onChange={(v) => v && setCurrency(v)} options={SANDBOX_CURRENCIES.map((c) => ({ value: c, label: c }))} /></div>
      <Button variant="secondary" size="sm" loading={creating} onClick={createSandbox} iconLeft={<FlaskConical size={13} />}>Create test ads account</Button>
    </div>}

    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
      <Button variant="ghost" size="sm" onClick={onRemove} iconLeft={<Unplug size={14} />}>Disconnect</Button>
      <div className="flex flex-wrap gap-2">
        {configured && (health?.status === 'expired' || !a.isActive) && <Button size="sm" onClick={onReconnect} iconLeft={<RefreshCw size={13} />}>Reconnect X</Button>}
        {canUse && !inUse && <Button variant="secondary" size="sm" onClick={onUse}>Use this connection</Button>}
        {a.isActive && <Button variant="secondary" size="sm" onClick={onPick}>{a.adsAccountId ? 'Change ads account' : 'Choose ads account'}</Button>}
      </div>
    </div>
  </Card>;
}

/** The ads accounts the connected X user can open; picking one saves it on the connection. */
function AdsAccountPicker({ account, onClose, onSaved }: { account: XAccount | null; onClose: () => void; onSaved: (a: XAccount) => Promise<void> }) {
  const [options, setOptions] = useState<XAdsAccountOption[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [chosen, setChosen] = useState('');
  const [saving, setSaving] = useState(false);
  const uid = account?.accountUid;

  useEffect(() => {
    if (!uid) return;
    let on = true;
    setOptions(null); setFailed(null); setChosen(account?.adsAccountId ?? '');
    xAdsService.adsAccounts(uid).then((list) => { if (on) setOptions(list); }).catch((e) => { if (on) setFailed(errMsg(e)); });
    return () => { on = false; };
  }, [uid, account?.adsAccountId]);

  const save = async () => {
    if (!uid || !chosen) return;
    setSaving(true);
    try { const saved = await xAdsService.select(uid, chosen); toast.success('Ads account selected'); forgetProviderConnection('x'); await onSaved(saved); }
    catch { /* toasted */ }
    finally { setSaving(false); }
  };

  return <Modal open={!!account} onClose={onClose} title={`Ads accounts for @${account?.screenName || account?.xUserId || ''}`}>
    {failed && <XAdsNotice title="Could not list ads accounts" error>{failed}</XAdsNotice>}
    {!options && !failed && <div className="skeleton h-32 rounded-xl" aria-label="Loading ads accounts" />}
    {options?.length === 0 && <p className="xa-muted">This X user has no ads account access. Ask an Account administrator on ads.x.com to add the user{account?.sandbox ? ', or create a test ads account from the Account page' : ''}.</p>}
    {!!options?.length && <div className="max-h-[50vh] space-y-2 overflow-y-auto" role="radiogroup" aria-label="Ads accounts">
      {options.map((o) => <label key={o.id} className={`xa-option ${chosen === o.id ? 'is-on' : ''}`}>
        <input type="radio" name="x-ads-account" className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-primary)]" checked={chosen === o.id} onChange={() => setChosen(o.id)} />
        <span className="min-w-0">
          <span className="block font-medium">{o.name || o.id}</span>
          <span className="xa-muted block">{[o.id, o.businessName, o.timezone, o.approvalStatus && label(o.approvalStatus)].filter(Boolean).join(' · ')}</span>
        </span>
      </label>)}
    </div>}
    <p className="xa-muted mt-3">The funding instrument that can pay is picked automatically; you can change it on the connection afterwards.</p>
    <div className="mt-4 flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={save} loading={saving} disabled={!chosen}>Use this ads account</Button>
    </div>
  </Modal>;
}
